import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { uploadMediaFile } from '@/hooks/useUpload';
import PortfolioGalleryFields from '@/components/feature/PortfolioGalleryFields';

interface PortfolioCategory {
  id: number;
  nom: string;
  parent_id?: number | null;
}

export default function NewPortfolioPage() {
  const { user } = useAuth();

  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [categories, setCategories] = useState<PortfolioCategory[]>([]);
  const [categorieId, setCategorieId] = useState('');

  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    detailssup: '',
    tags: '',
    prix: '',
    product_image: '',
  });

  const [imageUploading, setImageUploading] = useState(false);
  const [imageTaille, setImageTaille] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Galerie d'images additionnelles + courte vidéo du projet.
  const [gallery, setGallery] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Catégories de service du commerce (mêmes que la page « Gérer les catégories »),
  // servent à filtrer le portfolio sur le site public.
  useEffect(() => {
    if (!user) return;
    const commerceId = getCommerceId(user);
    if (!commerceId) return;
    supabase
      .from('categorieservices')
      .select('id, nom, parent_id, sort_order')
      .eq('idcommerce', commerceId)
      .order('sort_order', { ascending: true })
      .order('nom', { ascending: true })
      .then(({ data }) => setCategories((data as PortfolioCategory[]) || []));
  }, [user]);

  // Options hiérarchiques (Catégorie → Sous-catégorie → Sous-sous) indentées.
  const orderedCategoryOptions = (() => {
    const roots = categories.filter((c) => !c.parent_id);
    const opts: { id: number; label: string }[] = [];
    roots.forEach((r) => {
      opts.push({ id: r.id, label: r.nom });
      categories.filter((c) => c.parent_id === r.id).forEach((sub) => {
        opts.push({ id: sub.id, label: `— ${sub.nom}` });
        categories.filter((c) => c.parent_id === sub.id).forEach((ss) => {
          opts.push({ id: ss.id, label: `— — ${ss.nom}` });
        });
      });
    });
    return opts;
  })();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setImageUploading(true);
    try {
      const url = await uploadMediaFile(file, 'portfolio');
      if (url) {
        setFormData((prev) => ({ ...prev, product_image: url }));
        setImageTaille(file.size);
      }
    } catch {
      // silent
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titre.trim() || !user) return;
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase.from('portfolio').insert({
        titre: formData.titre.trim(),
        description: formData.description.trim() || formData.titre.trim(),
        detailssup: formData.detailssup.trim(),
        tags: formData.tags.trim() || null,
        prix: parseFloat(formData.prix) || 0,
        product_image: formData.product_image || '',
        images: gallery.length > 0 ? gallery.join('|') : null,
        video: videoUrl || null,
        categorie_id: categorieId ? parseInt(categorieId, 10) : 0,
        iframe: '',
        taille: imageTaille,
        owner: user.id,
        idcommerce: getCommerceId(user),
        pays: user.Pays || null,
        ville: user.Ville || null,
        slug: formData.titre.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      });
      if (error) throw error;
      setMsg({ type: 'success', text: 'Projet ajouté avec succès !' });
      setTimeout(() => navigate('/dashboard/services/portfolio'), 1000);
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors de la création' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Sub nav */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button onClick={() => navigate('/dashboard/services/portfolio')} className="px-4 py-2.5 bg-background-50 border border-background-200/70 text-foreground-600 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line mr-1"></i> Portfolio
        </button>
        <span className="text-sm font-bold text-foreground-950">Nouveau projet</span>
      </div>

      <div className="max-w-2xl">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">Ajouter un projet réalisé</h2>

        {msg && (
          <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
            {msg.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Image */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Photo de couverture</label>
            {formData.product_image ? (
              <div className="relative w-full h-48 rounded-lg overflow-hidden bg-background-100 mb-2">
                <img src={formData.product_image} alt="Aperçu" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => { setFormData((prev) => ({ ...prev, product_image: '' })); setImageTaille(0); }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ) : (
              <>
                <label
                  onClick={() => imageInputRef.current?.click()}
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-background-200/70 rounded-lg cursor-pointer hover:border-primary-300 transition-colors bg-background-50"
                >
                  {imageUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
                      <span className="text-sm text-primary-500 font-medium">Upload en cours...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <i className="ri-image-add-line text-2xl text-foreground-400"></i>
                      <span className="text-sm text-foreground-500">Cliquez pour ajouter une image</span>
                      <span className="text-xs text-foreground-400">PNG, JPG, WEBP</span>
                    </div>
                  )}
                </label>
                <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="absolute w-px h-px opacity-0 pointer-events-none" />
              </>
            )}
          </div>

          {/* Galerie d'images + courte vidéo */}
          <PortfolioGalleryFields
            gallery={gallery}
            onChangeGallery={setGallery}
            video={videoUrl}
            onChangeVideo={setVideoUrl}
          />

          {/* Titre */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Titre du projet *</label>
            <input
              type="text"
              value={formData.titre}
              onChange={(e) => handleChange('titre', e.target.value)}
              required
              placeholder="Ex: Refonte site e-commerce"
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Décrivez le projet..."
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
            />
          </div>

          {/* Détails supplémentaires */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Détails supplémentaires</label>
            <textarea
              value={formData.detailssup}
              onChange={(e) => handleChange('detailssup', e.target.value)}
              rows={3}
              placeholder="Informations complémentaires..."
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
            />
          </div>

          {/* Catégorie (filtre du portfolio sur le site public) */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Catégorie</label>
            <select
              value={categorieId}
              onChange={(e) => setCategorieId(e.target.value)}
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="">Aucune catégorie</option>
              {orderedCategoryOptions.map((opt) => (
                <option key={opt.id} value={String(opt.id)}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-foreground-400 mt-1">Permet aux visiteurs de filtrer vos réalisations par catégorie sur votre site public.</p>
          </div>

          {/* Prix */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Budget/Prix du projet (MAD)</label>
            <input
              type="number"
              value={formData.prix}
              onChange={(e) => handleChange('prix', e.target.value)}
              min="0"
              step="0.01"
              placeholder="0.00"
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-1.5">Tags (séparés par des virgules)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="Ex: web, design, mobile"
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving || !formData.titre.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Enregistrer
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard/services/portfolio')}
              className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}